import { queryOptions } from '@tanstack/react-query';

import type { Category } from '@/lib/queries/catalog';

import { supabase } from '@/lib/supabase';

// STREAMING BROWSE — data seam (PLAN §5, Increment 3).
//
// featuredVod / categoryRows / editorialRow are data-backed (Increment 3b-ii):
// anon reads on `content` plus two SECURITY DEFINER popularity RPCs
// (get_video_like_counts_14d, get_article_read_counts_14d) that expose 14-day
// aggregates over owner-private tables without leaking a user_id. All
// ranking/interleaving lives in pure exported helpers (rankCategoryRows,
// interleaveEditorial) so it unit-tests without a network, like selectHero.
//
// liveNow / recentlyLive / nextScheduled read Phase G public `rooms` and degrade
// to empty when there's nothing live/recent/scheduled. Categories and the menu
// bar are served by the catalog.ts seam, not re-stubbed here.

// --- shapes -----------------------------------------------------------------

export interface LiveStream {
  id: string;
  title: string;
  creatorName: string;
  creatorAvatarUrl: string | null;
  viewerCount: number;
  thumbnailUrl: string | null;
  watchHref: string;
}

export interface RecentlyLiveEntry {
  id: string;
  title: string;
  creatorName: string;
  creatorAvatarUrl: string | null;
  endedAt: string;
}

export interface ScheduledStream {
  id: string;
  title: string;
  scheduledFor: string;
}

export interface FeaturedVod {
  id: string;
  title: string;
  creatorName: string;
  excerpt: string | null;
  thumbnailUrl: string | null;
  watchHref: string;
}

export interface BrowseVideo {
  id: string;
  title: string;
  creatorName: string;
  thumbnailUrl: string | null;
  durationLabel: string | null;
  watchHref: string;
}

export interface CategoryRow {
  category: Category;
  videos: BrowseVideo[];
}

/**
 * A "continue watching" entry (Spec 34): the existing BrowseVideo shape — so the
 * rail reuses VideoCard unmodified, inheriting the freemium gate and lock chip —
 * plus the saved position and a computed percent for the progress bar.
 */
export interface ContinueWatchingItem {
  video: BrowseVideo;
  positionSeconds: number;
  durationSeconds: number | null;
  percent: number; // 0–100; 0 when duration is unknown
}

export interface EditorialItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  badge: 'NEW' | 'POPULAR';
}

/** Decoupled inputs for the pure ranking helpers (no supabase row shapes). */
export interface RankableVideo {
  id: string;
  title: string;
  slug: string;
  categoryId: string | null;
  thumbnailUrl: string | null;
  publishedAt: string | null;
  creatorName: string;
}

export interface RankableArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  publishedAt: string | null;
}

// --- pure helpers (unit-tested, no network) ---------------------------------

/**
 * The VOD watch path — `/videos/{slug}`, the marketing-facing URL contract. The
 * single place cards and the hero route through, so the whole surface points at
 * the watch page (Spec 16) from one seam.
 */
export function videoWatchHref(slug: string): string {
  return `/videos/${slug}`;
}

/** Forward-looking live watch path (mirrors videoWatchHref). G5: `/live/$slug`. */
export function liveWatchHref(slug: string): string {
  return `/live/${slug}`;
}

/** Normalized room-card input for the pure rail mappers (no supabase row shapes). */
export interface RoomCardRow {
  id: string;
  slug: string;
  title: string;
  startedAt: string | null;
  endedAt: string | null;
  host: { display_name: string | null; username: string | null; avatar_url: string | null } | null;
}

/** Pure: a currently-live room → a live card. No ccu in v1 (viewerCount stays 0). */
export function toLiveStream(row: RoomCardRow): LiveStream {
  return {
    id: row.id,
    title: row.title,
    creatorName: creatorDisplayName(row.host),
    creatorAvatarUrl: row.host?.avatar_url ?? null,
    viewerCount: 0,
    thumbnailUrl: null, // rooms have no thumbnail → UI falls back to avatar/initials
    watchHref: liveWatchHref(row.slug),
  };
}

/** Pure: a recently-ended room → a recently-live card (non-linked until G6 replay). */
export function toRecentlyLive(row: RoomCardRow): RecentlyLiveEntry {
  return {
    id: row.id,
    title: row.title,
    creatorName: creatorDisplayName(row.host),
    creatorAvatarUrl: row.host?.avatar_url ?? null,
    endedAt: row.endedAt ?? '',
  };
}

/** PostgREST embeds a to-one FK as an object, but normalize defensively. */
function normalizeHost(h: unknown): RoomCardRow['host'] {
  const row = Array.isArray(h) ? (h[0] ?? null) : h;
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  return {
    display_name: (r.display_name as string | null) ?? null,
    username: (r.username as string | null) ?? null,
    avatar_url: (r.avatar_url as string | null) ?? null,
  };
}

/** ISO timestamps sort lexically = chronologically; null is treated as oldest. */
function comparePublishedDesc(
  a: { publishedAt: string | null },
  b: { publishedAt: string | null },
): number {
  return (b.publishedAt ?? '').localeCompare(a.publishedAt ?? '');
}

export type HeroSelection =
  | { kind: 'live'; stream: LiveStream }
  | { kind: 'featured'; vod: FeaturedVod }
  | { kind: 'empty' };

/**
 * The hero shows a random currently-live stream, re-rolled per page load. With
 * no live streams it falls back to the admin-featured VOD; with neither it is
 * empty. `rand` is injectable so the re-roll is deterministically testable.
 */
export function selectHero(
  live: LiveStream[],
  featured: FeaturedVod | null,
  rand: () => number = Math.random,
): HeroSelection {
  if (live.length > 0) {
    const idx = Math.min(live.length - 1, Math.floor(rand() * live.length));
    return { kind: 'live', stream: live[idx]! };
  }
  if (featured) return { kind: 'featured', vod: featured };
  return { kind: 'empty' };
}

/**
 * Bucket videos by category, ordering each bucket by 14-day like count desc then
 * recency. Categories with no videos are dropped. `likeCounts` maps content_id →
 * 14-day likes; a video absent from it is 0 likes and falls to the recency tiebreak.
 */
export function rankCategoryRows(
  categories: Category[],
  videos: RankableVideo[],
  likeCounts: Map<string, number>,
): CategoryRow[] {
  return [...categories]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((category) => {
      const bucket = videos
        .filter((v) => v.categoryId === category.id)
        .sort((a, b) => {
          const likeDelta = (likeCounts.get(b.id) ?? 0) - (likeCounts.get(a.id) ?? 0);
          return likeDelta !== 0 ? likeDelta : comparePublishedDesc(a, b);
        })
        .map(
          (v): BrowseVideo => ({
            id: v.id,
            title: v.title,
            creatorName: v.creatorName,
            thumbnailUrl: v.thumbnailUrl,
            durationLabel: null, // duration enrichment is a scoped follow-up, not 3b-ii
            watchHref: videoWatchHref(v.slug),
          }),
        );
      return { category, videos: bucket };
    })
    .filter((row) => row.videos.length > 0);
}

/**
 * Produce the single mixed editorial row: newest articles (NEW) interleaved with
 * the most-read over 14 days (POPULAR), deduped so an article that is both
 * appears once with the POPULAR badge winning. `readCounts` maps content_id →
 * 14-day distinct readers. Capped at `limit`.
 */
export function interleaveEditorial(
  articles: RankableArticle[],
  readCounts: Map<string, number>,
  limit = 6,
): EditorialItem[] {
  const popularIds = new Set(
    articles.filter((a) => (readCounts.get(a.id) ?? 0) > 0).map((a) => a.id),
  );
  const newest = [...articles].sort(comparePublishedDesc);
  const popular = [...articles]
    .filter((a) => popularIds.has(a.id))
    .sort((a, b) => {
      const readDelta = (readCounts.get(b.id) ?? 0) - (readCounts.get(a.id) ?? 0);
      return readDelta !== 0 ? readDelta : comparePublishedDesc(a, b);
    });

  const out: EditorialItem[] = [];
  const seen = new Set<string>();
  const push = (a: RankableArticle) => {
    if (seen.has(a.id) || out.length >= limit) return;
    seen.add(a.id);
    out.push({
      id: a.id,
      slug: a.slug,
      title: a.title,
      excerpt: a.excerpt,
      badge: popularIds.has(a.id) ? 'POPULAR' : 'NEW', // POPULAR wins on dedupe
    });
  };

  const maxLen = Math.max(newest.length, popular.length);
  for (let i = 0; i < maxLen && out.length < limit; i++) {
    if (newest[i]) push(newest[i]!);
    if (popular[i]) push(popular[i]!);
  }
  return out;
}

function creatorDisplayName(
  creator: { display_name: string | null; username: string | null } | null,
): string {
  return creator?.display_name ?? creator?.username ?? 'ArcheTelos';
}

// --- continue-watching helpers (pure, unit-tested) --------------------------

/** Rows last watched before this window age out of the "continue watching" rail. */
export const CONTINUE_WATCHING_MAX_AGE_DAYS = 60;

/** The 60-day cutoff as an ISO string — the lower bound for `last_watched_at`. */
export function continueWatchingCutoffIso(now: Date): string {
  return new Date(now.getTime() - CONTINUE_WATCHING_MAX_AGE_DAYS * 86_400_000).toISOString();
}

/** Resume percent, 0–100. 0 when duration is unknown or non-positive. */
export function computeWatchPercent(
  positionSeconds: number,
  durationSeconds: number | null,
): number {
  if (durationSeconds == null || durationSeconds <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((positionSeconds / durationSeconds) * 100)));
}

/** `h:mm:ss` / `m:ss` duration label; null when there is no real duration. */
export function formatWatchDuration(seconds: number | null): string | null {
  if (seconds == null || seconds <= 0) return null;
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const ss = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Assemble a rail item from a card + its saved position (percent computed). */
export function toContinueWatchingItem(
  video: BrowseVideo,
  positionSeconds: number,
  durationSeconds: number | null,
): ContinueWatchingItem {
  return {
    video,
    positionSeconds,
    durationSeconds,
    percent: computeWatchPercent(positionSeconds, durationSeconds),
  };
}

// --- queries ----------------------------------------------------------------

export const browseQueries = {
  liveNow: () =>
    queryOptions({
      queryKey: ['browse', 'live-now'] as const,
      // Polling, not Realtime (G5 §2) — refresh the rail without a publication.
      refetchInterval: 4000,
      queryFn: async (): Promise<LiveStream[]> => {
        // Currently-live rooms by started_at desc (no ccu in v1).
        const { data, error } = await supabase
          .from('rooms')
          .select(
            'id, slug, title, started_at, host:profiles!rooms_host_id_fkey(display_name, username, avatar_url)',
          )
          .eq('status', 'live')
          .order('started_at', { ascending: false });
        if (error) throw error;
        return (data ?? []).map((r) =>
          toLiveStream({
            id: r.id,
            slug: r.slug,
            title: r.title,
            startedAt: r.started_at,
            endedAt: null,
            host: normalizeHost(r.host),
          }),
        );
      },
    }),

  recentlyLive: () =>
    queryOptions({
      queryKey: ['browse', 'recently-live'] as const,
      queryFn: async (): Promise<RecentlyLiveEntry[]> => {
        const { data, error } = await supabase
          .from('rooms')
          .select(
            'id, slug, title, ended_at, host:profiles!rooms_host_id_fkey(display_name, username, avatar_url)',
          )
          .eq('status', 'ended')
          .order('ended_at', { ascending: false })
          .limit(6);
        if (error) throw error;
        return (data ?? []).map((r) =>
          toRecentlyLive({
            id: r.id,
            slug: r.slug,
            title: r.title,
            startedAt: null,
            endedAt: r.ended_at,
            host: normalizeHost(r.host),
          }),
        );
      },
    }),

  nextScheduled: () =>
    queryOptions({
      queryKey: ['browse', 'next-scheduled'] as const,
      queryFn: async (): Promise<ScheduledStream | null> => {
        // Next upcoming scheduled room — same semantics as the marketing marquee
        // (status=scheduled, strictly-future start, soonest first). Render the
        // slot only when data exists.
        const nowIso = new Date().toISOString();
        const { data, error } = await supabase
          .from('rooms')
          .select('id, title, scheduled_start')
          .eq('status', 'scheduled')
          .gt('scheduled_start', nowIso)
          .order('scheduled_start', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        // scheduled_start is nullable in the schema; the gt filter already
        // excludes nulls, but guard so the slot only renders with a real time.
        if (!data?.scheduled_start) return null;
        return { id: data.id, title: data.title, scheduledFor: data.scheduled_start };
      },
    }),

  featuredVod: () =>
    queryOptions({
      queryKey: ['browse', 'featured-vod'] as const,
      queryFn: async (): Promise<FeaturedVod | null> => {
        const { data, error } = await supabase
          .from('content')
          .select(
            'id, title, excerpt, cover_image_url, slug, creator:profiles!content_creator_id_fkey(display_name, username)',
          )
          .eq('type', 'video')
          .eq('status', 'published')
          .eq('is_featured_vod', true)
          .order('featured_vod_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (!data) return null;
        return {
          id: data.id,
          title: data.title,
          creatorName: creatorDisplayName(data.creator),
          excerpt: data.excerpt,
          thumbnailUrl: data.cover_image_url,
          watchHref: videoWatchHref(data.slug),
        };
      },
    }),

  categoryRows: () =>
    queryOptions({
      queryKey: ['browse', 'category-rows'] as const,
      queryFn: async (): Promise<CategoryRow[]> => {
        const [categoriesRes, videosRes, likesRes] = await Promise.all([
          supabase
            .from('categories')
            .select('*')
            .eq('is_visible', true)
            .order('sort_order', { ascending: true }),
          supabase
            .from('content')
            .select(
              'id, title, slug, category_id, cover_image_url, published_at, creator:profiles!content_creator_id_fkey(display_name, username)',
            )
            .eq('type', 'video')
            .eq('status', 'published')
            .not('category_id', 'is', null)
            .order('published_at', { ascending: false }),
          supabase.rpc('get_video_like_counts_14d'),
        ]);
        if (categoriesRes.error) throw categoriesRes.error;
        if (videosRes.error) throw videosRes.error;
        if (likesRes.error) throw likesRes.error;

        const videos: RankableVideo[] = (videosRes.data ?? []).map((v) => ({
          id: v.id,
          title: v.title,
          slug: v.slug,
          categoryId: v.category_id,
          thumbnailUrl: v.cover_image_url,
          publishedAt: v.published_at,
          creatorName: creatorDisplayName(v.creator),
        }));
        const likeCounts = new Map(
          (likesRes.data ?? []).map((r) => [r.content_id, Number(r.like_count)]),
        );
        return rankCategoryRows(categoriesRes.data ?? [], videos, likeCounts);
      },
    }),

  editorialRow: () =>
    queryOptions({
      queryKey: ['browse', 'editorial-row'] as const,
      queryFn: async (): Promise<EditorialItem[]> => {
        const [articlesRes, readsRes] = await Promise.all([
          supabase
            .from('content')
            .select('id, slug, title, excerpt, published_at')
            .eq('type', 'article')
            .eq('status', 'published')
            .order('published_at', { ascending: false })
            .limit(12),
          supabase.rpc('get_article_read_counts_14d'),
        ]);
        if (articlesRes.error) throw articlesRes.error;
        if (readsRes.error) throw readsRes.error;

        const articles: RankableArticle[] = (articlesRes.data ?? []).map((a) => ({
          id: a.id,
          slug: a.slug,
          title: a.title,
          excerpt: a.excerpt,
          publishedAt: a.published_at,
        }));
        const readCounts = new Map(
          (readsRes.data ?? []).map((r) => [r.content_id, Number(r.reader_count)]),
        );
        return interleaveEditorial(articles, readCounts);
      },
    }),

  // Spec 34 — the "continue watching" rail. Filter and limit at the database: the
  // browse home must not pull a viewer's whole watch history to render six cards.
  // Own-row RLS also scopes to the user; the `!inner` embed + published/video
  // filters drop rows whose content was unpublished since it was watched.
  continueWatching: (userId: string | undefined) =>
    queryOptions({
      queryKey: ['browse', 'continue-watching', userId] as const,
      queryFn: async (): Promise<ContinueWatchingItem[]> => {
        if (!userId) return [];
        const { data, error } = await supabase
          .from('watch_progress')
          .select(
            'position_seconds, duration_seconds, content:content_id!inner(id, title, slug, cover_image_url, status, type, creator:profiles!content_creator_id_fkey(display_name, username))',
          )
          .eq('user_id', userId)
          .eq('completed', false)
          .gt('position_seconds', 0)
          .gt('last_watched_at', continueWatchingCutoffIso(new Date()))
          .eq('content.status', 'published')
          .eq('content.type', 'video')
          .order('last_watched_at', { ascending: false })
          .limit(6);
        if (error) throw error;
        return (data ?? []).map((row) => {
          const content = row.content as unknown as {
            id: string;
            title: string;
            slug: string;
            cover_image_url: string | null;
            creator: { display_name: string | null; username: string | null } | null;
          };
          const video: BrowseVideo = {
            id: content.id,
            title: content.title,
            creatorName: creatorDisplayName(content.creator),
            thumbnailUrl: content.cover_image_url,
            durationLabel: formatWatchDuration(row.duration_seconds),
            watchHref: videoWatchHref(content.slug),
          };
          return toContinueWatchingItem(video, row.position_seconds, row.duration_seconds);
        });
      },
      enabled: Boolean(userId),
    }),
};
